import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './ChatArchive.css';

/**
 * The chat archive: an administrator reading other people's conversations.
 *
 * Gordon: "The conversations and attachments in general should be saved and
 * admin can access them if necessary." Everything about this screen is
 * shaped by the last two words. The server (routes/chat-admin.js) refuses all
 * of it while the firm's switch is off, wants a reason for every look and
 * records each one; this screen's job is to make those three things obvious
 * to the person using it rather than surprising.
 *
 *   SWITCHED OFF, IT EXPLAINS ITSELF AND ASKS FOR NOTHING. The setting is
 *   read first, and while it is off the page says what the archive is and
 *   where it is switched on - and never asks the server for a conversation,
 *   not even the list. A page that fetched and showed a 403 would teach
 *   people that the archive is broken rather than that it is a decision.
 *
 *   THE REASON COMES BEFORE THE CONVERSATION. Choosing one opens a box
 *   asking why, and nothing is requested until there is an answer. It is the
 *   moment somebody decides whether they actually have a purpose, so it is
 *   a deliberate step rather than a field that can be filled in afterwards.
 *
 *   THE REASON STAYS ON SCREEN while the conversation is open, in a banner
 *   that says the look is on record - and the same reason is sent with every
 *   file opened from it, because opening a file is its own recorded look.
 *
 *   "WHO LOOKED" IS ALWAYS THERE, switched on or off, because the record of
 *   earlier looks is what an administrator or an auditor needs most on the
 *   day somebody asks whether their messages were read.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (iso) => (iso
  ? new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
  : '');

const size = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB`
  : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} bytes`);

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What a conversation is called in the list and above the reader. */
const titleOf = (c) => (c.kind === 'group'
  ? c.name
  : (c.people || []).map((p) => p.name).join(' and '));


/*
 * BRANCHES, EDITS AND DELETES (084), as the record keeps them: a branch's
 * replies straight under the message they answer (a reply also posted to the
 * conversation stays where it was said, marked so); an edited message with
 * its earlier wordings a click away; a deleted one with what it said and who
 * deleted it. Only here - the participants see "This message was deleted".
 */
const nestBranches = (messages) => {
  const under = new Map();
  const ids = new Set((messages || []).map((m) => m.id));
  for (const m of messages || []) {
    if (m.reply_to_id && !m.also_in_conversation && ids.has(m.reply_to_id)) {
      if (!under.has(m.reply_to_id)) under.set(m.reply_to_id, []);
      under.get(m.reply_to_id).push(m);
    }
  }
  const out = [];
  for (const m of messages || []) {
    if (m.reply_to_id && !m.also_in_conversation && ids.has(m.reply_to_id)) continue;
    out.push({ m, reply: false });
    for (const r of under.get(m.id) || []) out.push({ m: r, reply: true });
  }
  return out;
};

export default function ChatArchive() {
  const [enabled, setEnabled] = useState(null);   // null until known
  const [tab, setTab] = useState('conversations');
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [list, setList] = useState(null);

  const [asking, setAsking] = useState(null);     // the conversation a reason is being asked for
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(null);         // { conversation, messages, reason, kind }
  const [opening, setOpening] = useState(false);

  const [views, setViews] = useState(null);
  const reasonBox = useRef(null);

  /*
   * A 403 that says switched_off means somebody turned it off while this page
   * was open. The page drops back to its explanation rather than showing an
   * error, because that is what happened: a decision, not a fault.
   */
  const switchedOff = (err) => {
    if (err.response?.status === 403 && err.response?.data?.switched_off) {
      setEnabled(false);
      setOpen(null);
      setAsking(null);
      return true;
    }
    return false;
  };

  useEffect(() => {
    axios.get(`${API_BASE}/app-settings`)
      .then((r) => setEnabled(r.data?.chat_admin_review === true))
      .catch((e) => {
        setEnabled(false);
        setError(e.response?.data?.error || 'Could not read whether the archive is switched on.');
      });
  }, []);

  // The list, re-asked a moment after typing stops rather than on every key.
  useEffect(() => {
    if (enabled !== true || tab !== 'conversations') return undefined;
    const t = setTimeout(() => {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (kind) params.kind = kind;
      axios.get(`${API_BASE}/chat-admin/conversations`, { params })
        .then((r) => { setList(r.data?.conversations || []); setError(''); })
        .catch((e) => {
          if (!switchedOff(e)) setError(e.response?.data?.error || 'Could not list the conversations.');
        });
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [enabled, tab, q, kind]);

  const loadViews = useCallback(() => {
    axios.get(`${API_BASE}/chat-admin/views`)
      .then((r) => { setViews(r.data?.views || []); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Could not load who looked.'));
  }, []);

  useEffect(() => { if (tab === 'views') loadViews(); }, [tab, loadViews]);

  useEffect(() => { if (asking && reasonBox.current) reasonBox.current.focus(); }, [asking]);

  const choose = (c) => {
    setAsking(c);
    setReason('');
    setError('');
  };

  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 500;

  const read = async (e) => {
    e.preventDefault();
    if (!asking || !reasonOk) return;
    const why = reason.trim();
    setOpening(true);
    try {
      const path = asking.kind === 'group' ? 'groups' : 'direct';
      // A POST with the reason in its body, never ?reason= in the address:
      // an address ends up in access logs, proxy logs and browser history,
      // and a reason can itself be sensitive. The server ignores one sent
      // in the URL.
      const r = await axios.post(`${API_BASE}/chat-admin/${path}/${asking.id}`, { reason: why });
      setOpen({ ...r.data, reason: why, kind: asking.kind });
      setAsking(null);
      setError('');
    } catch (err) {
      if (!switchedOff(err)) setError(err.response?.data?.error || 'Could not open that conversation.');
    } finally {
      setOpening(false);
    }
  };

  /**
   * A file, fetched through axios so the interceptor signs it, with the SAME
   * reason the conversation was opened with - the server records the file as
   * a look of its own and needs to know why.
   */
  const download = async (file) => {
    try {
      const r = await axios.post(`${API_BASE}/chat-admin/files/${file.id}`,
        { reason: open.reason }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on a delay; revoking at once races the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      if (!switchedOff(err)) setError(`Could not download ${file.file_name}.`);
    }
  };

  return (
    <div className="container ca">
      <div className="page-header">
        <h1 className="page-title">Chat archive</h1>
        <p className="page-description">
          Other people’s General and group conversations, for when there is a reason to read them.
        </p>
      </div>

      <div className="ca-tabs" role="tablist" aria-label="Chat archive">
        <button type="button" role="tab" aria-selected={tab === 'conversations'}
          className="ca-tab" onClick={() => setTab('conversations')}>Conversations</button>
        <button type="button" role="tab" aria-selected={tab === 'views'}
          className="ca-tab" onClick={() => setTab('views')}>Who looked</button>
      </div>

      {error && <p className="ca-error" role="alert">{error}</p>}

      {tab === 'conversations' && enabled === false && (
        <section className="ca-off">
          <h2 className="ca-off-title">Reading other people’s conversations is switched off</h2>
          <p>
            Conversations in General and in groups, and the files sent in them, are kept. While this
            is switched on, an administrator can open one here - after saying why - and every look
            is recorded under “Who looked”: who, which conversation, why and when.
          </p>
          <p>
            An administrator can switch it on in <Link to="/settings/communication/conversation-review">Settings → Chat, calls &amp; meetings → Conversation review</Link>.
          </p>
          <p className="ca-warning">
            Reading staff’s messages is monitoring. Staff should be told - what may be read, why,
            and who can read it - before it is switched on.
          </p>
        </section>
      )}

      {tab === 'conversations' && enabled === true && (
        <div className={`ca-split${open || asking ? ' ca-reading' : ''}`}>
          <section className="ca-list-pane" aria-label="Conversations">
            <div className="ca-search">
              <input type="search" className="ca-input" value={q}
                placeholder="Search by person or group" aria-label="Search by person or group"
                onChange={(e) => setQ(e.target.value)} />
              <select className="ca-input ca-kind" value={kind} aria-label="Kind of conversation"
                onChange={(e) => setKind(e.target.value)}>
                <option value="">All</option>
                <option value="direct">Between two people</option>
                <option value="group">Groups</option>
              </select>
            </div>
            {list === null && <p className="ca-quiet">Loading…</p>}
            {list && list.length === 0 && <p className="ca-quiet">No conversations match.</p>}
            <ul className="ca-list">
              {(list || []).map((c) => (
                <li key={`${c.kind}:${c.id}`}>
                  <button type="button"
                    className={`ca-item${open?.conversation?.id === c.id ? ' ca-item-open' : ''}`}
                    onClick={() => choose(c)}>
                    <span className="ca-item-title">
                      {titleOf(c)}
                      {c.kind === 'group' && <span className="ca-chip">Group</span>}
                      {c.archived && <span className="ca-chip">Closed</span>}
                    </span>
                    <span className="ca-item-meta">
                      {[plural(c.message_count, 'message'),
                        c.file_count ? plural(c.file_count, 'file') : null,
                        c.kind === 'group' ? plural(c.member_count, 'member') : null]
                        .filter(Boolean).join(' · ')}
                    </span>
                    <span className="ca-item-meta">Last: {when(c.last_message_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="ca-reader" aria-label="Conversation">
            {!open && !asking && (
              <p className="ca-quiet">Choose a conversation. You will be asked why before it opens.</p>
            )}

            {asking && (
              <form className="ca-ask" role="dialog" aria-modal="false"
                aria-labelledby="ca-ask-title" onSubmit={read}>
                <h2 id="ca-ask-title" className="ca-ask-title">Why are you reading “{titleOf(asking)}”?</h2>
                <p className="ca-quiet">
                  Your name, this conversation, your reason and the time are recorded, and anybody
                  who can see “Who looked” can read them.
                </p>
                <textarea ref={reasonBox} className="ca-input ca-reason" rows={3} maxLength={500}
                  value={reason} aria-label="Reason" required
                  placeholder="For example: grievance HR-12, raised by one of them"
                  onChange={(e) => setReason(e.target.value)} />
                <div className="ca-ask-actions">
                  <button type="submit" className="btn btn-primary btn-small" disabled={!reasonOk || opening}>
                    {opening ? 'Opening…' : 'Read it'}
                  </button>
                  <button type="button" className="btn btn-plain btn-small" onClick={() => setAsking(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {open && !asking && (
              <>
                <button type="button" className="btn btn-plain btn-small ca-back" onClick={() => setOpen(null)}>
                  ← Back to the list
                </button>
                <p className="ca-banner" role="status">
                  You are reading this as an administrator. This look is recorded: <q>{open.reason}</q>
                </p>
                <h2 className="ca-reader-title">{titleOf(open.conversation)}</h2>
                {open.kind === 'group' && (
                  <p className="ca-quiet">
                    {(open.conversation.members || []).map((m) => m.name).join(', ')}
                  </p>
                )}
                {open.messages.length === 0 && <p className="ca-quiet">Nothing has been said here.</p>}
                <ol className="ca-messages">
                  {nestBranches(open.messages).map(({ m, reply }) => (
                    <li key={m.seq} className={`${m.kind === 'event' ? 'ca-event' : 'ca-message'}${reply ? ' ca-reply' : ''}`}>
                      <div className="ca-message-head">
                        {reply && <span className="ca-tag" title="A reply in a branch off the message above">↩ Branch</span>}
                        <span className="ca-author">{m.author_name}</span>
                        <time className="ca-time" dateTime={m.created_at}>{when(m.created_at)}</time>
                        {m.also_in_conversation && <span className="ca-tag">Branch reply, also posted</span>}
                        {m.edited_at && !m.deleted_at && <span className="ca-tag">Edited {when(m.edited_at)}</span>}
                        {m.deleted_at && (
                          <span className="ca-tag ca-tag-deleted">
                            Deleted{m.deleted_by_name ? ` by ${m.deleted_by_name}` : ''} {when(m.deleted_at)}
                          </span>
                        )}
                      </div>
                      {m.body && <p className="ca-body" data-deleted={m.deleted_at ? 'true' : undefined}>{m.body}</p>}
                      {(m.versions || []).length > 0 && (
                        <details className="ca-versions">
                          <summary>Earlier wording{m.versions.length === 1 ? '' : 's'} ({m.versions.length})</summary>
                          <ol>
                            {m.versions.map((v, i) => (
                              <li key={`${v.at}-${i}`}>
                                <span className="ca-time">Until {when(v.at)}</span>
                                <p className="ca-body">{v.body}</p>
                              </li>
                            ))}
                          </ol>
                        </details>
                      )}
                      {m.attachments?.length > 0 && (
                        <div className="ca-files">
                          {m.attachments.map((f) => (
                            <button key={f.id} type="button" className="ca-file"
                              title={`Download ${f.file_name} - this is recorded too`}
                              onClick={() => download(f)}>
                              <span className="ca-file-name">{f.file_name}</span>
                              <span className="ca-file-size">{size(f.byte_size)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        </div>
      )}

      {tab === 'views' && (
        <section className="ca-views" aria-label="Who looked">
          <p className="ca-quiet">
            Every conversation and file an administrator has opened here, newest first. Nobody can
            change or remove a line of it.
          </p>
          {views === null && <p className="ca-quiet">Loading…</p>}
          {views && views.length === 0 && <p className="ca-quiet">Nobody has looked at anything yet.</p>}
          <ul className="ca-view-list">
            {(views || []).map((v) => (
              <li key={v.id} className="ca-view">
                <div className="ca-view-head">
                  <span className="ca-author">{v.viewer_name}</span>
                  <time className="ca-time" dateTime={v.viewed_at}>{when(v.viewed_at)}</time>
                </div>
                <p className="ca-view-what">
                  <span className="ca-chip">{v.scope_kind === 'file' ? 'File' : v.scope_kind === 'group' ? 'Group' : 'Conversation'}</span>
                  {v.what}
                </p>
                <p className="ca-view-reason">{v.reason}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
